import {chooseBasicCourseSource} from "./basicCourseSourceSelection";
import { getBasicCourseVariantSource } from "./basicCourseTaskInfo";
import * as AbsoluteEquation from "../generators/dzial2_algebra/temat5_zadania_uzupelniajace_maturalne/podtemat2_maturalne_zamkniete_types/MaturaAbsoluteValueEquationClosedTask";
import { isBasicCourseTaskType, isBasicCourseTask } from "./basicCourseSourcePolicy";
import React from "react";
import { OFFICIAL_MATURA_SHEET_MODULES } from "./official/officialMaturaSheetRegistry";
import { getMaturaTaskTypeCodes } from "./maturaTaskClassification";
import { MaturaPdfPreviewEnabledContext } from "../components/MaturaPdfPreview";
import { MaturaReviewContext } from "../components/MaturaExamRunner/MaturaReviewContext";

import { getBasicCourseCatalog, getBasicSheetDate, getBasicCourseGroupSources } from "./basicCourseMetadata";
import { getBasicCourseGroupType, isBasicCourseGroup, usesEqualBasicCourseGeneratorWeights } from "./basicCourseTaskGroups";
import { withTaskSeed } from "./withTaskSeed";

export function buildBasicCourseCatalog() {
  const byType = new Map();
  Object.entries(OFFICIAL_MATURA_SHEET_MODULES)
    .filter(([id]) => id.startsWith("cke-p-"))
    .sort(([a], [b]) => getBasicSheetDate(b) - getBasicSheetDate(a))
    .forEach(([sheetId, module]) => {
      module.generatePagedTasks("original").forEach(({ task, meta }) => {
        const taskNo = String(meta.taskNo);
        const classification = module.classification?.find(row => String(row.taskNo) === taskNo) || meta;
        const codes = getMaturaTaskTypeCodes(task);
        const code = classification.code3 || classification.subtopicId?.replace(/_/g, ".");
        if (code && !codes.includes(code)) codes.unshift(code);
        if (!codes.length) throw new Error(`Missing basic course classification: ${sheetId}:${taskNo}`);
        const source = { sheetId, taskNo, dateKey: getBasicSheetDate(sheetId) };
        if (!byType.has(task.type)) byType.set(task.type, {
          type: task.type, codes: new Set(), sources: [],
          kind: meta.quizKind || (/OpenTask$/.test(task.type) || task.assessmentMode === "self" ? "open" : "closed"),
        });
        const entry = byType.get(task.type);
        codes.forEach(value => entry.codes.add(value));
        entry.sources.push(source);
      });
    });
  return new Map([...byType].map(([type, entry]) => [type, { ...entry, codes: [...entry.codes] }]));
}

function CourseInput({ module, task, answer, onChange, disabled, allowTaskCheck, reviewMode }) {
  const selfAssessed = task.assessmentMode === "self" || task.type === "MaturaBasic_CommonDenominatorRationalLinearEquation";
  const semiAutomatic = task.type === "MaturaBasic_CommonDenominatorRationalLinearEquation";
  const reviewAllowed = selfAssessed && answer?.courseWorkConfirmed === true;
  return <MaturaReviewContext.Provider value={{ interactive: true, reviewAllowed: reviewAllowed && (!semiAutomatic || allowTaskCheck),
    assessmentDisabled: disabled, mode: allowTaskCheck ? "training" : reviewMode }}>
    {selfAssessed && !reviewAllowed && (!semiAutomatic || allowTaskCheck) && <button type="button" className="btn-secondary" style={{fontSize:14,padding:"8px 12px",marginBottom:16}}
      disabled={disabled || (semiAutomatic && ![answer?.domain,answer?.equation,answer?.x].some(v=>String(typeof v === "object" ? v?.raw || "" : v || "").trim()))} onClick={() => onChange({ ...answer, courseWorkConfirmed: true, reviewOpened: true })}>
      {task.type === "MaturaBasic_CommonDenominatorRationalLinearEquation" ? "Sprawdź zadanie" : "Zakończyłem rozwiązywanie — przejdź do samooceny"}
    </button>}
    {module?.renderInput?.(task, answer, onChange, disabled)}
  </MaturaReviewContext.Provider>;
}

// Keep the full source identity: shared types can use different sheet engines.
export function createBasicCourseRegistry(fallbackRegistry, { strictLevel = false, allowTaskCheck = true, reviewMode = "quiz" } = {}) {
  const registry = { ...fallbackRegistry };
  getBasicCourseCatalog().forEach((entry, type) => {
    const moduleFor = task => task.basicCourseGroup === "MaturaAbsoluteValueEquationClosedTask" && task.data?.questionType
      ? AbsoluteEquation : OFFICIAL_MATURA_SHEET_MODULES[task.basicCourseSource?.sheetId];
    const call = (method, task, ...args) => {
      const module = moduleFor(task) || fallbackRegistry[type];
      return module?.[method]?.(task, ...args);
    };
    registry[type] = {
      generateTask: (options = {}) => {
        const grouped = options.courseGroup === true && isBasicCourseGroup(type);
        const sources = grouped ? getBasicCourseGroupSources(type) : entry.sources;
        const requested = options.courseSource || options.combinatoricsSource;
        const chooseSource = () => {
          // Two controlled course slots: sum (December) and product (June).
          // Explicit source requests still take precedence below.
          if (grouped && type === "MaturaAbsoluteValueEquationClosedTask" && Number.isInteger(options.courseSlot)) {
            const member = options.courseSlot % 2 === 0 ? "MaturaBasic_AbsoluteEquationTwoRootSum" : type;
            return sources.find(item => item.type === member) || sources[0];
          }
          return chooseBasicCourseSource(sources,{equalGeneratorWeights:grouped && usesEqualBasicCourseGeneratorWeights(type)});
        };
        const source = requested
          ? sources.find(item => item.sheetId === requested.sheetId && item.taskNo === String(requested.taskNo))
          : grouped
            ? options.seed == null ? chooseSource() : withTaskSeed(`${options.seed}:course-family`, chooseSource)
            : sources[0];
        if (!source) throw new Error(`Invalid basic course source: ${type}`);
        if (grouped && getBasicCourseGroupType(type) === "MaturaAbsoluteValueEquationClosedTask") {
          const generate = () => AbsoluteEquation.generateTask({ ...options, variant: "random" });
          const task = options.seed == null ? generate() : withTaskSeed(`${options.seed}:course-variant`, generate);
          return { ...task, kind: "closed", data: { ...task.data, maxPoints: 1 },
            source: { variant: "random", exam: "MATURA", level: "basic", taskNumber: source.taskNo,
              session: source.sheetId === "cke-p-2024-grudzien" ? "2024_GRU" : "2025_CZE" },
            basicCourseSource: { sheetId: source.sheetId, taskNo: source.taskNo, dateKey: source.dateKey },
            basicCourseGroup: "MaturaAbsoluteValueEquationClosedTask" };
        }
        const module = OFFICIAL_MATURA_SHEET_MODULES[source.sheetId];
        const generateRow = () => module.generateTaskVariant
          ? module.generateTaskVariant(source.taskNo, "random", { ...options, single: true })
          : module.generatePagedTasks("random", options).find(item => String(item.meta.taskNo) === source.taskNo);
        const row = grouped && options.seed != null
          ? withTaskSeed(`${options.seed}:course-variant`, generateRow) : generateRow();
        if (!row?.task || row.task.type !== (source.type || type)) throw new Error(`Missing basic course generator: ${type}`);
        const { displayBaseNumber, maturaGroupId, maturaGroupPart, ...task } = row.task;
        return { ...task, kind: getBasicCourseCatalog().get(task.type)?.kind || entry.kind,
          data: { ...task.data, maxPoints: row.meta.maxPoints || task.points || 1 },
          source: getBasicCourseVariantSource({ ...task, basicCourseSource: source }),
          basicCourseSource: { sheetId: source.sheetId, taskNo: source.taskNo, dateKey: source.dateKey },
          ...(grouped ? { basicCourseGroup: getBasicCourseGroupType(type) } : {}) };
      },
      renderTask: task => <MaturaPdfPreviewEnabledContext.Provider value={false}>
        {call("renderTask", task)}
      </MaturaPdfPreviewEnabledContext.Provider>,
      renderInput: (task, answer, onChange, disabled) => <CourseInput
        module={moduleFor(task) || fallbackRegistry[type]} task={task} answer={answer}
        onChange={onChange} disabled={disabled} allowTaskCheck={allowTaskCheck} reviewMode={reviewMode} />,
      checkAnswer: (task, ...args) => call("checkAnswer", task, ...args),
      renderAnswer: task => call("renderAnswer", task),
      renderExplanation: task => call("renderExplanation", task),
      renderUserAnswer: (answer, task, ...args) => (moduleFor(task) || fallbackRegistry[type])?.renderUserAnswer?.(answer, task, ...args),
    };
  });
  if (strictLevel) {
    Object.keys(registry).forEach(type => {
      if (!isBasicCourseTaskType(type)) { delete registry[type]; return; }
      const generate = registry[type].generateTask;
      registry[type] = { ...registry[type], generateTask: (...args) => {
        const task = generate(...args);
        if (!isBasicCourseTask(task)) throw new Error(`Non-basic task in basic course: ${type}`);
        return task;
      } };
    });
  }
  return registry;
}
